import shouldSkipProcessRow from './helpers/shouldSkipProcessRow';
import shouldSkipProcessReadTombstonedRow from './helpers/shouldSkipProcessReadTombstonedRow';
import fetchRichContentForRows from './helpers/fetchRichContentForRows';
import type { MailListRowDataType, TableView } from 'owa-mail-list-store';
import { getRowKeyFromListViewType } from 'owa-mail-list-store';
import updateInstrumentationContext from 'owa-mail-list-store/lib/utils/updateInstrumentationContext';
import TableOperations from 'owa-mail-list-table-operations';
import type { InstrumentationContext } from 'owa-search-types/lib/types/InstrumentationContext';
import { mutatorAction } from 'satcheljs';
import type { ListViewLoadingDirection } from 'owa-mail-loading-action-types';
import { isUnifiedInboxFolder } from 'owa-unified-inbox-data-utils/lib/isUnifiedInboxFolder';
import preserveExistingDraftInstanceKey from './helpers/preserveExistingDraftInstanceKey';

/**
 * Process FindConversation/FindItem response and add them to the beginning or end of the given TableView
 * @param tableView to add results in
 * @param rows to be added
 * @param totalRowsInViewInResponse - total rows in the table returned from server
 * @param source - source of addition
 * @param searchResponseId - the search query id
 * @param doNotOverwriteData determines if updates should be written (default is false)
 * @param isStitchingRows - flag to indicate if we are stitching rows from the server into the existing table.
 */
export default function addRowResponse(
    tableView: TableView,
    rows: MailListRowDataType[],
    totalRowsInViewInResponse: number,
    source: string = '',
    searchResponseId?: string,
    searchLogicalId?: string,
    doNotOverwriteData: boolean = false,
    listViewLoadingDirection: ListViewLoadingDirection = 'NextPage',
    hasNextPage: boolean = true,
    shouldHonorTombstonedReadStateOnStitch: boolean = false
): void {
    const isAppending = listViewLoadingDirection === 'NextPage';
    const rowList: MailListRowDataType[] = (isAppending ? rows : rows.reverse()) || [];
    let failedCount = 0;

    for (const responseRow of rowList) {
        const row = preserveExistingDraftInstanceKey(responseRow, tableView);
        // Check if row should be processed
        if (shouldSkipProcessRow(row, tableView)) {
            continue;
        }

        // Since we are stitching the rows from the server into the existing table, we want to honor the tombstone entry for the Read reason
        // to prevent flashing of read/unread states in the UI.
        if (
            shouldHonorTombstonedReadStateOnStitch &&
            shouldSkipProcessReadTombstonedRow(row, tableView)
        ) {
            continue;
        }

        let wasSuccessful = true;
        // Add all rows to beginning or end of table
        if (
            TableOperations.containsRowKey(
                tableView,
                getRowKeyFromListViewType(row, tableView.tableQuery.listViewType)
            )
        ) {
            const rowUpdatedIndex = isAppending ? tableView.rowKeys.length - 1 : 0;

            // Move row to new position in table if it already exists. There could be some timing issue
            // where the row was moved down on the server. In this case we want to keep the server's latest order
            TableOperations.updateRowMutator(
                rowUpdatedIndex,
                row,
                tableView,
                source,
                doNotOverwriteData,
                'RowLoadMore'
            );
        } else {
            const rowNewIndex = isAppending ? tableView.rowKeys.length : 0;
            wasSuccessful = TableOperations.addRowMutator(
                rowNewIndex,
                row,
                tableView,
                source,
                doNotOverwriteData,
                'RowLoadMore'
            );
        }

        // Account for failures
        if (!wasSuccessful) {
            failedCount++;
        }

        if (searchResponseId && row.InstanceKey) {
            const instrumentationContext = <InstrumentationContext>{
                referenceKey: row.ReferenceKey,
                index: tableView.rowKeys.length - 1,
                traceId: searchResponseId,
                logicalId: searchLogicalId,
            };

            updateInstrumentationContext(row.InstanceKey, tableView, instrumentationContext);
        }
    }

    // VSO 1285: optimize this number on empty folder
    setTotalRowsInViewMutator(tableView, totalRowsInViewInResponse - failedCount, hasNextPage);

    fetchRichContentForRows(tableView);
}

export const setTotalRowsInViewMutator: (
    tableView: TableView,
    totalRowsInView: number,
    hasNextPage: boolean
) => void = mutatorAction(
    'setTotalRowsInView',
    (tableView: TableView, totalRowsInView: number, hasNextPage: boolean): void => {
        // Unified Inbox uses IDB data and the total number of rows in view is what
        // In case there is no next page to load from UnifiedInbox, we set the totalRowsInView to the number of rows in the table
        // so we don't keep trying to load more rows.
        if (isUnifiedInboxFolder(tableView.tableQuery.folderId) && !hasNextPage) {
            tableView.totalRowsInView = tableView.rowKeys.length;
        } else {
            // If hasNextPage is false, we set totalRowsInView to the value returned
            tableView.totalRowsInView = totalRowsInView;
        }
    }
);
